/**
 * 長形式（1行1件）データ表 — RevoGrid 代替。
 * スタイルは KeisukeUnifiedDataGrid と揃え、行は @tanstack/react-virtual で仮想化。
 */

import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DataRow, InputFormTypeKey } from '../../../../stores/systems/keisuke/dashboardStore';
import { normalizeKeisukeNumericInput } from '../../../../utils/keisukeInputFormUtils';
import {
  clipboardLooksLikeSpreadsheetGrid,
  clipboardPlainTextFromEvent,
  parseClipboardTsvMatrix,
} from './keisukeDashboardClipboardUtils';
import grid from './KeisukeUnifiedDataGrid.module.scss';
import paneStyles from './KeisukeDashboardCenterPane.module.scss';
import KeisukeClipboardHintBar from './KeisukeClipboardHintBar';
import { gridDataHasNumericValues } from '../../../../utils/keisukeGridData';
import {
  clampKeisukeDashboardDataColWidthPx,
  KEISUKE_DASHBOARD_ROW_NUM_COL_WIDTH_PX,
  keisukeDashboardFillDataColWidthPx,
} from './keisukeDashboardDataGridLayout';

/** 仮想行の推定高さ（.keisukeAnovaGridInputCell の padding + line-height に合わせる） */
const LONG_FORMAT_ROW_ESTIMATE_PX = 38;
const LONG_FORMAT_ROW_NUM_WIDTH_PX = KEISUKE_DASHBOARD_ROW_NUM_COL_WIDTH_PX;

function applyMatrixToGrid(
  current: DataRow[],
  headers: string[],
  startRow: number,
  startCol: number,
  matrix: string[][]
): DataRow[] {
  const next = current.map((r) => ({ ...r }));
  for (let r = 0; r < matrix.length; r++) {
    const rowIndex = startRow + r;
    while (next.length <= rowIndex) {
      const newRow: DataRow = {};
      headers.forEach((h) => {
        newRow[h] = '';
      });
      next.push(newRow);
    }
    const row = next[rowIndex];
    const prow = matrix[r];
    if (!Array.isArray(prow)) continue;
    for (let c = 0; c < prow.length; c++) {
      const colIndex = startCol + c;
      const prop = headers[colIndex];
      if (prop) row[prop] = normalizeKeisukeNumericInput(String(prow[c] ?? ''));
    }
  }
  return next;
}

/** 行 -1 = ヘッダー、0 以上 = データ行 */
type LongFmtCell = { row: number; col: number };
type LongFmtSelection = { a: LongFmtCell; b: LongFmtCell };

function normalizeLongFmtSelection(s: LongFmtSelection): {
  minRow: number;
  maxRow: number;
  minCol: number;
  maxCol: number;
} {
  return {
    minRow: Math.min(s.a.row, s.b.row),
    maxRow: Math.max(s.a.row, s.b.row),
    minCol: Math.min(s.a.col, s.b.col),
    maxCol: Math.max(s.a.col, s.b.col),
  };
}

function longFmtSelectionCellCount(n: ReturnType<typeof normalizeLongFmtSelection>): number {
  return (n.maxRow - n.minRow + 1) * (n.maxCol - n.minCol + 1);
}

function longFmtCellInSelection(row: number, col: number, sel: LongFmtSelection | null): boolean {
  if (!sel) return false;
  const n = normalizeLongFmtSelection(sel);
  return row >= n.minRow && row <= n.maxRow && col >= n.minCol && col <= n.maxCol;
}

function longFmtCellFromTarget(target: EventTarget | null): LongFmtCell | null {
  const el = target as HTMLElement | null;
  if (!el?.closest) return null;
  if (el.closest('[data-lf-ignore-selection="1"]')) return null;
  const inp = el.closest('input[data-lf-row]') as HTMLInputElement | null;
  if (!inp) return null;
  const rs = inp.getAttribute('data-lf-row');
  const cs = inp.getAttribute('data-lf-col');
  if (rs == null || cs == null) return null;
  const row = parseInt(rs, 10);
  const col = parseInt(cs, 10);
  if (Number.isNaN(col) || col < 0) return null;
  if (Number.isNaN(row) || row < -1) return null;
  return { row, col };
}

function getLongFmtPasteAnchor(sel: LongFmtSelection | null, focus: LongFmtCell): LongFmtCell {
  if (!sel) return focus;
  const n = normalizeLongFmtSelection(sel);
  if (longFmtSelectionCellCount(n) <= 1) return focus;
  return { row: n.minRow, col: n.minCol };
}

function buildTsvFromLongFmtSelection(
  headerDrafts: string[],
  gridData: DataRow[],
  gridHeaders: string[],
  norm: ReturnType<typeof normalizeLongFmtSelection>
): string {
  const lines: string[][] = [];
  for (let r = norm.minRow; r <= norm.maxRow; r++) {
    const line: string[] = [];
    for (let c = norm.minCol; c <= norm.maxCol; c++) {
      if (r < 0) {
        line.push(headerDrafts[c] ?? '');
      } else {
        const prop = gridHeaders[c];
        const v = prop != null ? gridData[r]?.[prop] : undefined;
        line.push(v != null && String(v) !== '' ? String(v) : '');
      }
    }
    lines.push(line);
  }
  return lines.map((ln) => ln.join('\t')).join('\n');
}

export interface KeisukeDashboardLongFormatTableProps {
  gridData: DataRow[];
  gridHeaders: string[];
  disabled: boolean;
  longFormatTwoColWidthPx: number;
  onLinkedDataColumnWidthCommit: (px: number) => void;
  onDataChange: (next: DataRow[]) => void;
  onRenameColumn: (colIndex: number, rawName: string) => boolean;
  reactKey: string;
  /** 長形式スタック表の説明文切替（用量反応・生存・反復測定で文言を変える） */
  inputFormTypeForHelp?: InputFormTypeKey;
}

export default function KeisukeDashboardLongFormatTable({
  gridData,
  gridHeaders,
  disabled,
  longFormatTwoColWidthPx,
  onLinkedDataColumnWidthCommit,
  onDataChange,
  onRenameColumn,
  reactKey,
  inputFormTypeForHelp = 'longFormat',
}: KeisukeDashboardLongFormatTableProps) {
  const { t } = useTranslation();
  const fieldIdPrefix = useId();
  const longTableDescription = (() => {
    switch (inputFormTypeForHelp) {
      case 'doseResponseForm':
        return t('keisuke.dashboard.inputForm.doseResponseLongTableDescription');
      case 'survivalForm':
        return t('keisuke.dashboard.inputForm.survivalLongTableDescription');
      case 'repeatedForm':
        return t('keisuke.dashboard.inputForm.repeatedLongTableDescription');
      default:
        return t('keisuke.dashboard.inputForm.longFormatDescription');
    }
  })();
  const twoCols = gridHeaders.length === 2;
  const [colWidthPx, setColWidthPx] = useState(longFormatTwoColWidthPx);
  /** ユーザーが列境界をドラッグしたら true。それまではビューポート幅に合わせて列を広げる */
  const [userResizedColWidth, setUserResizedColWidth] = useState(false);
  const [viewportInnerW, setViewportInnerW] = useState(0);
  const colDragRef = useRef<{ startX: number; startW: number } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const [gridSelection, setGridSelection] = useState<LongFmtSelection | null>(null);
  const [isDragSelecting, setIsDragSelecting] = useState(false);
  const gridAnchorRef = useRef<LongFmtCell | null>(null);

  useEffect(() => {
    setColWidthPx(longFormatTwoColWidthPx);
  }, [longFormatTwoColWidthPx]);

  useEffect(() => {
    setUserResizedColWidth(false);
  }, [reactKey, gridHeaders.length]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      setViewportInnerW((prev) => (Math.abs(prev - w) < 0.5 ? prev : w));
    });
    ro.observe(el);
    setViewportInnerW(el.clientWidth);
    return () => ro.disconnect();
  }, [reactKey]);

  const hasNumericData = gridDataHasNumericValues(gridHeaders, gridData);
  const fillColWidth = keisukeDashboardFillDataColWidthPx(
    viewportInnerW,
    gridHeaders.length || 1
  );
  /** 未リサイズ時はビューポート優先（空は composition 幅内） */
  const dataColWidth = userResizedColWidth && twoCols ? colWidthPx : fillColWidth;

  /** 自動拡幅を他入力形式と共有（空の幅はストアへ書き戻さない） */
  useEffect(() => {
    if (!hasNumericData || userResizedColWidth || !twoCols || viewportInnerW <= 0) return;
    if (dataColWidth === longFormatTwoColWidthPx) return;
    onLinkedDataColumnWidthCommit(dataColWidth);
  }, [
    dataColWidth,
    hasNumericData,
    longFormatTwoColWidthPx,
    onLinkedDataColumnWidthCommit,
    twoCols,
    userResizedColWidth,
    viewportInnerW,
  ]);

  const rowVirtualizer = useVirtualizer({
    count: gridData.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => LONG_FORMAT_ROW_ESTIMATE_PX,
    overscan: 24,
  });

  const [headerDrafts, setHeaderDrafts] = useState<string[]>(() => [...gridHeaders]);
  useEffect(() => {
    setHeaderDrafts([...gridHeaders]);
  }, [gridHeaders]);

  const updateCell = useCallback(
    (rowIndex: number, prop: string, value: string) => {
      const v = normalizeKeisukeNumericInput(value);
      const next = gridData.map((row, i) => (i === rowIndex ? { ...row, [prop]: v } : row));
      onDataChange(next);
    },
    [gridData, onDataChange]
  );

  const onHeaderBlur = useCallback(
    (colIndex: number) => {
      const raw = headerDrafts[colIndex] ?? '';
      const ok = onRenameColumn(colIndex, raw);
      if (!ok) {
        setHeaderDrafts((prev) => {
          const n = [...prev];
          n[colIndex] = gridHeaders[colIndex] ?? '';
          return n;
        });
      }
    },
    [headerDrafts, gridHeaders, onRenameColumn]
  );

  const applyLongFmtClipboard = useCallback(
    (e: React.ClipboardEvent, anchor: LongFmtCell) => {
      if (disabled) return;
      const text = clipboardPlainTextFromEvent(e);
      if (!clipboardLooksLikeSpreadsheetGrid(text)) return;
      e.preventDefault();
      const matrix = parseClipboardTsvMatrix(text);
      if (matrix.length === 0) return;

      if (anchor.row < 0) {
        const firstLine = matrix[0] ?? [];
        const nextDrafts = [...headerDrafts];
        for (let j = 0; j < firstLine.length; j++) {
          const ci = anchor.col + j;
          if (ci < nextDrafts.length) nextDrafts[ci] = firstLine[j] ?? '';
        }
        setHeaderDrafts(nextDrafts);
        const rest = matrix.slice(1);
        if (rest.length > 0) {
          onDataChange(applyMatrixToGrid(gridData, gridHeaders, 0, anchor.col, rest));
        }
        queueMicrotask(() => {
          for (let j = 0; j < firstLine.length; j++) {
            const ci = anchor.col + j;
            if (ci < gridHeaders.length) onRenameColumn(ci, nextDrafts[ci] ?? '');
          }
        });
        return;
      }
      onDataChange(applyMatrixToGrid(gridData, gridHeaders, anchor.row, anchor.col, matrix));
    },
    [disabled, gridData, gridHeaders, headerDrafts, onDataChange, onRenameColumn]
  );

  const onPasteAt = useCallback(
    (e: React.ClipboardEvent, focusRow: number, focusCol: number) => {
      const focus: LongFmtCell = { row: focusRow, col: focusCol };
      const anchor = getLongFmtPasteAnchor(gridSelection, focus);
      applyLongFmtClipboard(e, anchor);
    },
    [applyLongFmtClipboard, gridSelection]
  );

  const addRow = useCallback(() => {
    const empty: DataRow = {};
    gridHeaders.forEach((h) => {
      empty[h] = '';
    });
    onDataChange([...gridData, empty]);
  }, [gridData, gridHeaders, onDataChange]);

  useEffect(() => {
    if (!isDragSelecting) return;
    const onMove = (e: PointerEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const cell = longFmtCellFromTarget(el);
      if (cell && gridAnchorRef.current) {
        setGridSelection({ a: gridAnchorRef.current, b: cell });
      }
    };
    const onUp = () => setIsDragSelecting(false);
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp, true);
    document.addEventListener('pointercancel', onUp, true);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp, true);
      document.removeEventListener('pointercancel', onUp, true);
    };
  }, [isDragSelecting]);

  useEffect(() => {
    const onDocDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (tableRef.current?.contains(t)) return;
      setGridSelection(null);
      gridAnchorRef.current = null;
    };
    document.addEventListener('mousedown', onDocDown, true);
    return () => document.removeEventListener('mousedown', onDocDown, true);
  }, []);

  const onTablePointerDownCapture = (e: React.PointerEvent<HTMLTableElement>) => {
    if (disabled || e.button !== 0) return;
    const cell = longFmtCellFromTarget(e.target);
    if (!cell) return;
    if (e.shiftKey && gridAnchorRef.current) {
      setGridSelection({ a: gridAnchorRef.current, b: cell });
      return;
    }
    gridAnchorRef.current = cell;
    setGridSelection({ a: cell, b: cell });
    setIsDragSelecting(true);
  };

  /** Ctrl+C は copy イベントで clipboardData に載せる（keydown で preventDefault + writeText だと未設定環境でコピーが空になる） */
  const onTableCopyCapture = (e: React.ClipboardEvent<HTMLTableElement>) => {
    if (disabled) return;
    const t = e.target as HTMLElement;
    if (t.tagName !== 'INPUT') return;
    const focusedCell = longFmtCellFromTarget(t);
    const effectiveSel =
      gridSelection ??
      (focusedCell ? ({ a: focusedCell, b: focusedCell } satisfies LongFmtSelection) : null);
    if (!effectiveSel) return;
    const norm = normalizeLongFmtSelection(effectiveSel);
    const area = longFmtSelectionCellCount(norm);
    if (area <= 1) {
      const inp = t as HTMLInputElement;
      if (
        inp.selectionStart != null &&
        inp.selectionEnd != null &&
        inp.selectionStart !== inp.selectionEnd
      ) {
        return;
      }
    }
    e.preventDefault();
    e.stopPropagation();
    const tsv = buildTsvFromLongFmtSelection(headerDrafts, gridData, gridHeaders, norm);
    e.clipboardData?.setData('text/plain', tsv);
  };

  const onTableKeyDownCapture = (e: React.KeyboardEvent<HTMLTableElement>) => {
    if (disabled) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      setGridSelection(null);
      gridAnchorRef.current = null;
    }
  };

  const gridCellClass = (row: number, col: number) =>
    longFmtCellInSelection(row, col, gridSelection) ? grid.tTestTwoColCellSelected : undefined;

  const clearColResizeChrome = () => {
    document.body.style.removeProperty('user-select');
    document.body.style.removeProperty('cursor');
  };

  const onColResizePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (disabled || !twoCols) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    setUserResizedColWidth(true);
    colDragRef.current = { startX: e.clientX, startW: dataColWidth };
  };

  const onColResizePointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const sess = colDragRef.current;
    if (!sess) return;
    const dx = e.clientX - sess.startX;
    setColWidthPx(clampKeisukeDashboardDataColWidthPx(sess.startW + dx));
  };

  const onColResizePointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    const sess = colDragRef.current;
    if (sess) {
      const finalW = clampKeisukeDashboardDataColWidthPx(e.clientX - sess.startX + sess.startW);
      setColWidthPx(finalW);
      onLinkedDataColumnWidthCommit(finalW);
    }
    colDragRef.current = null;
    clearColResizeChrome();
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
  };

  const onColResizeLostCapture = () => {
    colDragRef.current = null;
    clearColResizeChrome();
  };

  const tableGridWidthPx =
    LONG_FORMAT_ROW_NUM_WIDTH_PX + gridHeaders.length * dataColWidth;

  /** 数値投入後は表自体が説明になるので「表の見方」帯を出さない */
  const showTableHelp = !gridDataHasNumericValues(gridHeaders, gridData);

  return (
    <div
      className={`${paneStyles.formWrap} ${paneStyles.formWrapDense} ${paneStyles.longFormatTableRoot}`}
      key={reactKey}
    >
      {showTableHelp ? (
        <details className={paneStyles.formHintDetails}>
          <summary className={paneStyles.formHintSummary}>
            {t('keisuke.dashboard.inputForm.tableHelpSummary')}
          </summary>
          <p className={paneStyles.formDescription}>{longTableDescription}</p>
        </details>
      ) : null}
      <div ref={scrollRef} className={paneStyles.longFormatVirtualViewport}>
        <table
          ref={tableRef}
          className={`${grid.keisukeAnovaGridTable} ${grid.keisukeAnovaGridTableAlignCenter} ${paneStyles.longFormatVirtualTable}`}
          style={{
            width: tableGridWidthPx,
            maxWidth: 'none',
          }}
          onPointerDownCapture={onTablePointerDownCapture}
          onCopyCapture={onTableCopyCapture}
          onKeyDownCapture={onTableKeyDownCapture}
        >
          <colgroup>
            <col className={grid.keisukeRowNumCol} />
            {gridHeaders.map((_, c) => (
              <col key={c} style={{ width: `${dataColWidth}px` }} />
            ))}
          </colgroup>
          <thead style={{ width: tableGridWidthPx }}>
            <tr>
              <th className={grid.keisukeRowNumCell} aria-hidden>#</th>
              {gridHeaders.map((h, c) => {
                const isResizeHost = twoCols && c === 0;
                return (
                  <th
                    key={`h-${c}-${h}`}
                    className={
                      [isResizeHost ? grid.keisukeTTestColResizeHost : '', gridCellClass(-1, c) ?? '']
                        .filter(Boolean)
                        .join(' ') || undefined
                    }
                  >
                    <input
                      type="text"
                      className={grid.keisukeAnovaGridInputHeader}
                      id={`${fieldIdPrefix}-lf-h-${c}`}
                      name={`${fieldIdPrefix}-lf-h-${c}`}
                      data-lf-row={-1}
                      data-lf-col={c}
                      value={headerDrafts[c] ?? ''}
                      disabled={disabled}
                      onChange={(e) =>
                        setHeaderDrafts((prev) => {
                          const n = [...prev];
                          n[c] = e.target.value;
                          return n;
                        })
                      }
                      onBlur={() => onHeaderBlur(c)}
                      onPaste={(e) => onPasteAt(e, -1, c)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          (e.target as HTMLInputElement).blur();
                        }
                      }}
                      aria-label={t('keisuke.dashboard.columnNameAria', {
                        column: h,
                      })}
                    />
                    {isResizeHost && (
                      <button
                        type="button"
                        className={grid.keisukeTTestColResizeHandle}
                        data-lf-ignore-selection="1"
                        tabIndex={-1}
                        disabled={disabled}
                        aria-label={t('keisuke.dashboard.inputForm.resizeLongFormatColumns')}
                        onPointerDown={onColResizePointerDown}
                        onPointerMove={onColResizePointerMove}
                        onPointerUp={onColResizePointerUp}
                        onPointerCancel={onColResizePointerUp}
                        onLostPointerCapture={onColResizeLostCapture}
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody style={{ height: `${rowVirtualizer.getTotalSize()}px`, width: tableGridWidthPx }}>
            {rowVirtualizer.getVirtualItems().map((vr) => {
              const rowIndex = vr.index;
              const row = gridData[rowIndex];
              return (
                <tr
                  key={vr.key}
                  className={paneStyles.longFormatVirtualTr}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: tableGridWidthPx,
                    height: `${vr.size}px`,
                    transform: `translateY(${vr.start}px)`,
                  }}
                >
                  <td className={grid.keisukeRowNumCell} aria-hidden>
                    {rowIndex + 1}
                  </td>
                  {gridHeaders.map((prop, colIndex) => (
                    <td key={`${rowIndex}-${prop}`} className={gridCellClass(rowIndex, colIndex)}>
                      <input
                        type="text"
                        className={grid.keisukeAnovaGridInputCell}
                        id={`${fieldIdPrefix}-lf-c-${rowIndex}-${colIndex}`}
                        name={`${fieldIdPrefix}-lf-c-${rowIndex}-${colIndex}`}
                        data-lf-row={rowIndex}
                        data-lf-col={colIndex}
                        value={row[prop] != null ? String(row[prop]) : ''}
                        disabled={disabled}
                        onChange={(e) => updateCell(rowIndex, prop, e.target.value)}
                        onPaste={(e) => onPasteAt(e, rowIndex, colIndex)}
                        aria-label={`${prop} ${rowIndex + 1}`}
                      />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className={paneStyles.formActions}>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          disabled={disabled}
          onClick={addRow}
        >
          {t('keisuke.dashboard.inputForm.addRow')}
        </button>
      </div>
      <KeisukeClipboardHintBar />
    </div>
  );
}
